/**
 * Audit Log API Service
 * 
 * Handles audit log API calls for USER_LOGIN and USER_LOGOUT events
 * This is the only audit logging method - all other static logging has been removed
 */

import api from '@/axios';

class AuditLogService {
  private sessionId: string;
  private loggedLoginEvents: Set<string> = new Set();

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  /**
   * Generate a unique session identifier
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  /**
   * Call the audit log API endpoint for USER_LOGIN and USER_LOGOUT events
   * This is the only audit logging method - calls the backend API
   * 
   * @param action - Must be 'USER_LOGIN' or 'USER_LOGOUT'
   * @param sessionId - Optional session identifier
   * @param metadata - Optional additional metadata
   * @returns Promise that resolves when the API call completes (or fails silently)
   */
  async logAuditEvent(
    action: 'USER_LOGIN' | 'USER_LOGOUT',
    sessionId?: string,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    try {
      if (action === 'USER_LOGIN') {
        const eventKey = `${action}-${sessionId || this.sessionId}`;
        if (this.loggedLoginEvents.has(eventKey)) {
          console.log(`[Audit Log] USER_LOGIN already logged for this session, skipping duplicate`);
          return;
        }
        this.loggedLoginEvents.add(eventKey);
      }

      const requestBody: {
        action: 'USER_LOGIN' | 'USER_LOGOUT';
        sessionId?: string;
        metadata?: Record<string, unknown>;
      } = {
        action,
      };

      if (sessionId) {
        requestBody.sessionId = sessionId;
      }

      if (metadata) {
        requestBody.metadata = metadata;
      }

      console.log(`[Audit Log] Calling API for ${action}:`, requestBody);
      
      const response = await api.post('/audit-log', requestBody);
      
      console.log(`[Audit Log] API call successful for ${action}:`, response.status, response.data);
    } catch (error: any) {
      console.error(`[Audit Log] Failed to log ${action} event:`, error);
      if (error.response) {
        console.error(`[Audit Log] Response status:`, error.response.status);
        console.error(`[Audit Log] Response data:`, error.response.data);
      } else if (error.request) {
        console.error(`[Audit Log] Request was made but no response received:`, error.request);
        console.error(`[Audit Log] Request URL:`, error.config?.url);
        console.error(`[Audit Log] Request method:`, error.config?.method);
      } else {
        console.error(`[Audit Log] Error setting up request:`, error.message);
      }
      
      if (action === 'USER_LOGIN' && sessionId) {
        const eventKey = `${action}-${sessionId}`;
        this.loggedLoginEvents.delete(eventKey);
      }
    }
  }

  /**
   * Get the current session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }
}

// Export singleton instance
export const auditLogService = new AuditLogService();
